import Image from "next/image";
import { toWebp } from "@/utils/common";

interface ApproachProps {
  data?: any;
  isMobile?: any;
}

const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL ?? "";

export default function Approach({ data, isMobile }: ApproachProps) {
  if (!data?.SetApart || !Array.isArray(data.SetApart)) return null;

  // Split SetApart into groups of 3 cards
  const groups = [];
  for (let i = 0; i < data.SetApart.length; i += 3) {
    groups.push(data.SetApart.slice(i, i + 3));
  }

  return (
    <div className="bg-background-dark text-white relative">
      <section className="bg-background-white xl:py-25 md:py-[80px] py-[56px] relative">
        <div className="container mx-auto px-4 relative z-10">
          {/* Heading */}
          <h2 className="text-heading-1 font-bold text-center capitalize max-w-[812px] mx-auto">
            {data?.title}{" "}
          </h2>
          <p className="text-secondary-text-white text-subtitle-2 text-center mt-[10px] mb-[40px] max-w-[812px] mx-auto">
            {data?.description}
          </p>

          {/* Dynamic Grid Rendering */}
          {groups.map((group, groupIndex) => {
            const [first, second, third] = group;
            const isEven = groupIndex % 2 === 0; // alternate layout

            return (
              <div
                key={groupIndex}
                className="grid grid-cols-1 md:grid-cols-2 gap-[30px] mb-[30px]"
              >
                {/* Even groups: tall card left, stacked right */}
                {isEven ? (
                  <>
                    {first && <TallCard item={first} />}
                    <div className="flex flex-col gap-[30px]">
                      {second && <SmallCard item={second} />}
                      {third && <SmallCard item={third} />}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Odd groups: stacked left, tall right */}
                    <div className="flex flex-col gap-[30px]">
                      {first && <SmallCard item={first} />}
                      {second && <SmallCard item={second} />}
                    </div>
                    {third && <TallCard item={third} />}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Background Images */}
        {!isMobile &&
          <div
            className="absolute top-0 left-0 right-0 z-0 w-full bg-no-repeat hidden md:block h-full"
            style={{
              backgroundImage: "url('/images/consulting/bg-approch.png')",
              backgroundSize: "100% 100%",
            }}
          ></div>
        }
        {isMobile &&
          <div
            className="absolute w-full h-full top-0 left-0 right-0 bottom-0 z-0 bg-no-repeat bg-cover bg-top block md:hidden"
            style={{
              backgroundImage: "url('/images/consulting/bg-approch-mobile.png')",
            }}
          ></div>
        }
      </section>
    </div>
  );
}

/* ================== SUB COMPONENTS ================== */

function TallCard({ item }: { item: any }) {
  return (
    <div className="border border-[#32343B] group bg-background-dark relative card-approch overflow-hidden min-h-[626px] max-h-[626px]">
      {/* Text section */}
      <div className="flex flex-col items-start min-h-[273px] max-h-[273px] justify-start text-left flex-1 p-[32px_24px_56px_24px] lg:p-[32px_30px_56px_30px] card-hover-approch relative group">
        <div className="y-overflow-auto h-[310px]">
          <h3
            className="text-secondary-text-white 
            xl:translate-y-[45px] xl:group-hover:translate-y-0 
            xl:text-heading-4 md:text-heading-6 
            font-bold mb-[12px] xl:mb-[24px] 
            xl:group-hover:text-heading-6 max-w-[540px] 
            transition-all duration-500 ease-in-out"
          >
            {item?.short_title}
          </h3>

          <p className="text-secondary-text-white text-subtitle-2 max-w-[540px] xl:opacity-0 xl:translate-y-[20px] xl:group-hover:opacity-100 xl:group-hover:translate-y-0 transition-all duration-500 ease-in-out delay-150">
            {item?.title}
          </p>
        </div>
      </div>

      {/* Image section */}
      {item?.image?.data?.attributes?.url && (
        <div className="h-[400px] overflow-hidden">
          <Image
            src={toWebp(baseUrl + item.image.data.attributes.url)}
            alt={item?.title}
            width={600}
            height={400}
            quality={100}
            className="w-full h-full object-cover transition-all duration-700 ease-out transform group-hover:scale-105 group-hover:-translate-y-2"
          />
        </div>
      )}

      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out pointer-events-none"></div>
    </div>
  );
}

function SmallCard({ item }: { item: any }) {
  return (
    <div className="border border-[#32343B] group relative card-approch overflow-hidden min-h-[297px] max-h-[297px]">
      <div className="y-overflow-auto h-[310px]">
        <div
          className="flex flex-col justify-center items-start text-left flex-1 p-[32px_24px_56px_24px] lg:p-[32px_30px_56px_30px]
          card-hover-approch relative group xl:justify-center xl:group-hover:justify-start transition-all duration-500 ease-in-out"
        >
          <h4
            className="text-secondary-text-white 
            xl:translate-y-[45px] 
            xl:opacity-100
            xl:group-hover:translate-y-0 
            xl:group-hover:opacity-100
            xl:text-heading-4 md:text-heading-6 
            font-bold mb-[12px] xl:mb-[24px] 
            xl:group-hover:text-heading-6 
            max-w-[540px] 
            transition-all duration-500 ease-in-out"
          >
            {item?.short_title}
          </h4>

          <p
            className="text-secondary-text-white text-subtitle-2 max-w-[540px] xl:opacity-0 xl:translate-y-[20px] 
            xl:group-hover:opacity-100 xl:group-hover:translate-y-0 transition-all duration-500 ease-in-out delay-150"
          >
            {item?.title}
          </p>
        </div>
      </div>

      <Image
        src="/images/consulting/hover.png"
        alt=""
        height={165}
        width={220}
        className="absolute right-[-46%] lg:right-[-26%] md:right-[-36%] xl:right-0 bottom-0 transition-all duration-600 ease-out delay-200 opacity-100 translate-x-0 translate-y-0 
          xl:opacity-0 xl:translate-x-[40px] xl:translate-y-[20px] xl:group-hover:opacity-100 xl:group-hover:translate-x-0 xl:group-hover:translate-y-0"
      />
    </div>
  );
}
